import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCar, FaCalendarAlt, FaFileAlt, FaEnvelope, FaComments, FaBug, FaMinusCircle, FaUserPlus, FaArchive, FaRoute } from 'react-icons/fa';
import '../css/Sidebar.css';
import { authAPI } from '../api/api';
import { usePermissions } from '../hooks/usePermissions';
import { useChat } from '../context/ChatContext';
import MessagingService from '../services/MessagingService';

const messagingService = new MessagingService();

const Sidebar = ({ isCollapsed, setIsCollapsed }) => {
  const [showBugHelper, setShowBugHelper] = React.useState(false);
  const navigate = useNavigate();
  const [user, setUser] = React.useState(null);
  const { permissions } = usePermissions();
  const { unreadCounts } = useChat();
  const [messagesUnreadCount, setMessagesUnreadCount] = React.useState(0);

  const menuItems = [
    { to: '/', icon: <FaCar className="sidebar-icon" />, label: 'Autóim' },
    { to: '/appointments', icon: <FaCalendarAlt className="sidebar-icon" />, label: 'Időpontok/Határidők' },
    ...(permissions.can_see_documents ? [{ to: '/documents', icon: <FaFileAlt className="sidebar-icon" />, label: 'Dokumentumok' }] : []),
    { to: '/crash-report', icon: <FaMinusCircle className="sidebar-icon" />, label: 'Kárbejelentő' },
    { to: '/messages', icon: <FaEnvelope className="sidebar-icon" />, label: 'Levelezés' },
    { to: '/chat', icon: <FaComments className="sidebar-icon" />, label: 'Chat' },
    { to: '/archive', icon: <FaArchive className="sidebar-icon" />, label: 'Archív autók' },
    { to: '/route', icon: <FaRoute className="sidebar-icon" />, label: 'Menetlevél kiállítás' },
  ];

  useEffect(() => {
    const user = authAPI.getCurrentUser();
    if (user) {
      setUser(user);
      messagingService.setClientUserId(user.id);
    }
  }, []);

  // Olvasatlan levelek számának lekérése
  useEffect(() => {
    const fetchMessagesUnread = async () => {
      try {
        const stats = await messagingService.getFolderStats();
        const totalUnread = stats.inbox?.unread || 0;
        setMessagesUnreadCount(totalUnread);
      } catch (error) {
        console.error('Error fetching message stats:', error);
      }
    };

    if (user) {
      fetchMessagesUnread();
      // Frissítés 30 másodpercenként
      const interval = setInterval(fetchMessagesUnread, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 1024) {
        setIsCollapsed(true);
      } else {
        setIsCollapsed(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setIsCollapsed]);

  const currentPath = window.location.pathname;

  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 900);
  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleMenuClick = (to) => {
    navigate(to);
    if (isMobile) setIsCollapsed(true);
  };

  const sidebarClass = `sidebar${isCollapsed ? ' sidebar-closed' : ''}${isCollapsed && isMobile ? ' sidebar-hidden' : ''}`;

  // Összes olvasatlan chat üzenet számítása
  const totalChatUnread = Object.values(unreadCounts || {}).reduce((sum, count) => sum + count, 0);

  return (
    <div className={sidebarClass}>
      <div className="sidebar-header"></div>
      <ul className="sidebar-menu">
        {menuItems.map((item, idx) => {
          let badgeCount = 0;
          if (item.to === '/messages') {
            badgeCount = messagesUnreadCount;
          } else if (item.to === '/chat') {
            badgeCount = totalChatUnread;
          }

          return (
            <li
              key={item.to}
              className={currentPath === item.to ? 'active' : ''}
              onClick={() => handleMenuClick(item.to)}
              style={{ cursor: 'pointer', position: 'relative' }}
            >
              {item.icon} {!isCollapsed && <span>{item.label}</span>}
              {badgeCount > 0 && (
                <span className="sidebar-badge">{badgeCount > 99 ? '99+' : badgeCount}</span>
              )}
            </li>
          );
        })}
        {user && (user.role === 'admin') && (
          <li
            className={currentPath === '/user-management' ? 'active' : ''}
            onClick={() => handleMenuClick('/user-management')}
          >
            <FaUserPlus className="sidebar-icon" /> {!isCollapsed && <span>Felhasználók kezelése</span>}
          </li>
        )}
      </ul>
      <div className="sidebar-bug-wrapper">
        <button
          className="sidebar-bug-btn"
          onMouseEnter={() => setShowBugHelper(true)}
          onMouseLeave={() => setShowBugHelper(false)}
          onClick={() => navigate('/bug-report')}
        >
          <FaBug />
          {showBugHelper && !isCollapsed && <span className="sidebar-bug-helper">Hibabejelentés</span>}
        </button>
      </div>
    </div>
  );
};

export default Sidebar; 